import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { toApiDayEnd, toApiDayStart } from '../alarms-api';

export interface ReachAgent {
  id: number;
  name: string;
  description?: string;
  elevenlabs_agent_id: string;
  system_prompt: string;
  voice_id: string;
  language: string;
  first_message: string;
  type: string;
  is_active: boolean;
  max_call_duration_seconds: number;
  telecentrum_number?: string | null;
  telecentrum_label?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReachPhoneNumber {
  id: number;
  phone_number: string;
  label: string;
  elevenlabs_phone_number_id: string;
  current_agent_id: number;
  is_active: boolean;
  created_at: string;
}

export interface ReachTrigger {
  id: number;
  agent_id: number;
  phone_number_id: number;
  type: string;
  event_type: string;
  cron_expression: string;
  target_group: string;
  target_patient_ids: number[];
  priority: number;
  is_active: boolean;
  condition_id?: number | null;
  condition_params?: any;
  poll_interval_minutes?: number;
  call_cooldown_hours?: number;
  night_pause?: number;
  last_triggered_at: string;
  created_at: string;
}

export interface ReachCallLog {
  id: number;
  agent_id: number;
  trigger_id: number;
  patient_id: number;
  conversation_id: string;
  to_number: string;
  from_number: string;
  status: string;
  direction: string;
  duration_seconds: number;
  cost_credits: number;
  initiated_at: string;
  completed_at: string;
  termination_reason: string;
  transcript_summary?: string;
  sentiment?: string;
  transcript?: {
    full_transcript: any[];
    summary: string;
    sentiment: string;
    agent_turns: number;
    user_turns: number;
    interruptions_count: number;
    audio_url: string;
    evaluation: any;
  };
}

export interface ReachConfig {
  enabled: boolean;
  maxAgents: number;
  currentAgents: number;
  maxPhoneNumbers: number;
  currentPhoneNumbers: number;
  monthlyCreditsLimit: number;
  creditsUsedThisMonth: number;
  webhookSecret: string;
}

export interface ReachCallStats {
  total_calls: number;
  completed: number;
  failed: number;
  no_answer: number;
  avg_duration: number;
  total_cost: number;
}

@Injectable()
export class ReachService {

  private basePath = '/manager/reach';

  constructor(private api: ApiService, private http: HttpClient) {}

  // ==================== CONFIG ====================

  getConfig(): Observable<ReachConfig> {
    return this.api.get(`${this.basePath}/config`);
  }

  // ==================== AGENTS ====================

  getAgents(): Observable<ReachAgent[]> {
    return this.api.get(`${this.basePath}/agents`);
  }

  getAgent(id: number): Observable<ReachAgent> {
    return this.api.get(`${this.basePath}/agents/${id}`);
  }

  createAgent(data: Partial<ReachAgent>): Observable<ReachAgent> {
    return this.api.post(`${this.basePath}/agents`, data);
  }

  updateAgent(id: number, data: Partial<ReachAgent>): Observable<ReachAgent> {
    return this.api.put(`${this.basePath}/agents/${id}`, data);
  }

  deleteAgent(id: number): Observable<any> {
    return this.api.delete(`${this.basePath}/agents/${id}`);
  }

  testCall(agentId: number, toNumber: string, dynamicVariables?: any): Observable<any> {
    return this.api.post(`${this.basePath}/agents/${agentId}/test-call`, {
      to_number: toNumber,
      dynamic_variables: dynamicVariables || {}
    });
  }

  // Multi-call testowy: dzwoni do wielu numerów naraz.
  //  mode='queued'    → api2 kolejkuje w reach_trigger_queue, daemon rozdzwania paczkę ≤30s (bezpieczne tempo)
  //  mode='immediate' → api2 od razu strzela do ElevenLabs w pętli (test rate-limitów)
  // Zwraca {mode, queued|initiated, skipped, total, ...}.
  multiCall(
    agentId: number,
    numbers: string[],
    mode: 'queued' | 'immediate',
    opts?: {
      language?: string;
      dynamic_variables?: any;
      // Pacjent per numer (imei) — api2 zapisuje go na sztywno w historii połączeń,
      // żeby zmiana numeru pacjenta nie gubiła go w historii
      recipients?: Array<{ number: string; imei?: string }>;
    }
  ): Observable<any> {
    return this.api.post(`${this.basePath}/agents/${agentId}/multi-call`, {
      numbers,
      mode,
      language: opts?.language,
      dynamic_variables: opts?.dynamic_variables || {},
      recipients: opts?.recipients
    });
  }

  // ==================== PHONE NUMBERS ====================

  getPhoneNumbers(): Observable<ReachPhoneNumber[]> {
    return this.api.get(`${this.basePath}/phone-numbers`);
  }

  createPhoneNumber(data: any): Observable<ReachPhoneNumber> {
    return this.api.post(`${this.basePath}/phone-numbers`, data);
  }

  deletePhoneNumber(id: number): Observable<any> {
    return this.api.delete(`${this.basePath}/phone-numbers/${id}`);
  }

  // ==================== TRIGGERS ====================

  // Katalog warunków (dev-authored) dla triggerów warunkowych — id, key, name, description, param_schema.
  getTriggerConditions(): Observable<any[]> {
    return this.api.get(`${this.basePath}/trigger-conditions`);
  }

  getTriggers(): Observable<ReachTrigger[]> {
    return this.api.get(`${this.basePath}/triggers`);
  }

  createTrigger(data: Partial<ReachTrigger>): Observable<ReachTrigger> {
    return this.api.post(`${this.basePath}/triggers`, data);
  }

  updateTrigger(id: number, data: Partial<ReachTrigger>): Observable<ReachTrigger> {
    return this.api.put(`${this.basePath}/triggers/${id}`, data);
  }

  deleteTrigger(id: number): Observable<any> {
    return this.api.delete(`${this.basePath}/triggers/${id}`);
  }

  toggleTrigger(id: number): Observable<ReachTrigger> {
    return this.api.post(`${this.basePath}/triggers/${id}/toggle`);
  }

  // ==================== CALL LOGS ====================

  getCallLogs(page = 1, limit = 20, filters: any = {}): Observable<{ data: ReachCallLog[], pagination: any }> {
    const params: any = { page: page.toString(), limit: limit.toString() };
    if (filters.status) params.status = filters.status;
    if (filters.agent_id) params.agent_id = filters.agent_id;
    if (filters.date_from) params.date_from = toApiDayStart(filters.date_from);
    if (filters.date_to) params.date_to = toApiDayEnd(filters.date_to);
    return this.api.get(`${this.basePath}/call-logs`, params);
  }

  getCallLogDetail(id: number): Observable<ReachCallLog> {
    return this.api.get(`${this.basePath}/call-logs/${id}`);
  }

  // Nagranie rozmowy (mp3) z ElevenLabs — proxy przez api2 (klucz EL zostaje na serwerze).
  // Zwraca Blob; front gra przez URL.createObjectURL. 404 z backendu = brak nagrania.
  getCallLogAudio(id: number): Observable<Blob> {
    return this.http.get(`${environment.api_url}${this.basePath}/call-logs/${id}/audio`, {
      responseType: 'blob'
    });
  }

  // agentId zawęża eksport do jednego agenta (#315). String(), bo HttpParams w Angularze 11
  // przyjmuje wyłącznie wartości tekstowe.
  exportCallLogsCsv(dateFrom?: string, dateTo?: string, agentId?: number): Observable<Blob> {
    const params: any = {};
    if (dateFrom) params.date_from = toApiDayStart(dateFrom);
    if (dateTo) params.date_to = toApiDayEnd(dateTo);
    if (agentId) params.agent_id = String(agentId);
    return this.http.get(`${environment.api_url}${this.basePath}/export-csv`, {
      params,
      responseType: 'blob'
    });
  }

  getCallStats(dateFrom?: string, dateTo?: string): Observable<ReachCallStats> {
    const params: any = {};
    if (dateFrom) params.date_from = toApiDayStart(dateFrom);
    if (dateTo) params.date_to = toApiDayEnd(dateTo);
    return this.api.get(`${this.basePath}/call-stats`, params);
  }
}
